import {Button, Segment} from 'platform/components';
import {Heading, HStack, Show, Text, VStack} from 'platform/foundation';

import {SalesLeaguePeriod} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {SkeletonLoadingBox} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {SALES_LEAGUE_PERIOD_LABEL, SALES_LEAGUE_PERIODS} from '../constants/SALES_LEAGUE_PERIODS';

const SUBTITLE_SKELETON_WIDTH = 50;
const LINE_HEIGHT = 3;

interface SalesLeagueBoardHeaderProps extends RequiredTestIdProps {
  heading: string;
  /** Period range and the size of the board, e.g. "1. 9. – 4. 9. · 14 reps with sales". */
  subtitle: string;
  /** The subtitle counts rows, so it is skeletoned until the board arrives. */
  isLoading?: boolean;
  period: SalesLeaguePeriod;
  onPeriodChange: (period: SalesLeaguePeriod) => void;
  /** Downloads the board the header sits on, for the period the switcher shows. */
  onExport: () => void;
  isExporting?: boolean;
}

export function SalesLeagueBoardHeader(props: SalesLeagueBoardHeaderProps) {
  const periodOptions = SALES_LEAGUE_PERIODS.map((periodValue) => ({
    value: periodValue,
    label: i18n.t(SALES_LEAGUE_PERIOD_LABEL[periodValue]),
  }));

  return (
    // Wrapping, not shrinking: narrow, the controls drop to their own line.
    <HStack justify="space-between" align="flex-end" spacing={4} wrap>
      <VStack spacing={1}>
        <Text size="xSmall" color="tertiary">
          {i18n.t('page.salesLeague.eyebrow')}
        </Text>
        <Heading size={2}>{props.heading}</Heading>
        <Show when={props.isLoading}>
          <SkeletonLoadingBox isLoading width={SUBTITLE_SKELETON_WIDTH} height={LINE_HEIGHT} />
        </Show>
        <Show when={!props.isLoading}>
          <Text size="small" color="tertiary">
            {props.subtitle}
          </Text>
        </Show>
      </VStack>
      <HStack spacing={2} align="center">
        <Segment<SalesLeaguePeriod>
          value={props.period}
          options={periodOptions}
          onChange={props.onPeriodChange}
          data-testid={suffixTestId('period', props)}
        />
        {/* The export asks the backend for the board again, so it stays out of reach until
            the board on screen is real — there is no period to export before then. */}
        <Button
          variant="secondary"
          leftIcon="file/download"
          title={i18n.t('general.actions.export')}
          onClick={props.onExport}
          isDisabled={props.isLoading}
          isLoading={props.isExporting}
          data-testid={suffixTestId('export', props)}
        />
      </HStack>
    </HStack>
  );
}
